/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnInit,
	Optional,
	Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { ActualPhase, PlanAccessLevel, ProjectMember, Ticket } from '@touchplan/common';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, pluck, scan, takeUntil } from 'rxjs/operators';
import {
	CombinedTicketTypes,
	EditTicketsCombined,
	TicketDropdownTypes,
} from 'src/app/app-data/models/EditTicketsCombined';
import { PlanState } from 'src/app/services/plan-state.service';
import { ConstraintAcceptanceService } from '../../constraint-acceptance.service';
import { EditOverlay, TicketEditService } from '../../ticket-edit.service';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';

import { CONSTRAINT_ACCEPTANCE_STATE, TicketType } from '@touchplan/common';
import { DefaultLoggerService } from '@touchplan/logging';
import * as AppStrings from '@touchplan/strings';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';

interface ActionMenuItem {
	key: string;
	text: string; //default text
	multiText?: string; //text in multiple mode
	singleText?: string; //text in single mode for things that have multi-text
	icon?: string;
	iconImage?: string;
	impactedTickets?: number; //generated in the update method
	show: boolean;
	disabled: boolean;
	click?: (item?: any) => void;
}

/**
 * Displays a menu of actions for the ticket.  Available actions
 * vary with the state of the ticket.
 *
 * More detail...
 */
@Component({
	selector: 'ticket-action-menu',
	templateUrl: './ticket-action-menu.component.html',
	styleUrls: ['./ticket-action-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('TicketActionMenuComponent')],
})
export class TicketActionMenuComponent extends TicketEditBaseComponent implements OnInit {
	// @Input() editTicketCombined: EditTicketsCombined;
	@Input('editTicketCombined')
	set etc(val: EditTicketsCombined) {
		this.inputSubject$.next(val);
	}

	// tslint:disable: max-line-length
	public list: ActionMenuItem[] = [
		{
			key: 'viewAttachments',
			text: AppStrings.TICKET_EDIT_VIEW_ATTACHMENTS,
			icon: 'attach_file',
			show: false,
			disabled: false,
			click: (item) => {
				this.viewAttachments();
			},
		},
		{
			key: 'addPin',
			text: AppStrings.TICKET_EDIT_ADD_PIN,
			icon: 'add_circle',
			show: false,
			disabled: false,
			click: (item) => {
				this.addPin();
			},
		},
		{
			key: 'removePin',
			text: AppStrings.TICKET_EDIT_REMOVE_PIN,
			icon: 'do_not_disturb_on',
			show: false,
			disabled: false,
			click: (item) => {
				this.removePin();
			},
		},
		{
			key: 'moreDetails',
			text: AppStrings.TICKET_EDIT_MORE_DETAILS,
			icon: 'more_horiz',
			show: false,
			disabled: false,
			click: (item) => {
				this.constraintMoreDetails();
			},
		},
		{
			key: 'linkToProcore',
			text: AppStrings.TICKET_EDIT_LINK_TO_PROCORE,
			icon: 'link',
			show: false,
			disabled: false,
			click: (item) => {
				this.constraintLinkToProcore();
			},
		},
		{
			key: 'procore',
			text: AppStrings.TICKET_EDIT_PROCORE,
			icon: 'link',
			show: false,
			disabled: false,
			click: (item) => this.showProcore(),
		},
		{
			key: 'addToTimeline',
			text: AppStrings.TICKET_EDIT_ADD_TO_TIMELINE,
			icon: 'visibility',
			show: false,
			disabled: false,
			click: (item) => {
				this.toggleOnTimeline();
			},
		},
		{
			key: 'removeFromTimeline',
			text: AppStrings.TICKET_EDIT_REMOVE_FROM_TIMELINE,
			icon: 'visibility_off',
			show: false,
			disabled: false,
			click: (item) => {
				this.toggleOffTimeline();
			},
		},
		{
			key: 'changePrimaryRole',
			text: AppStrings.TICKET_EDIT_CHANGE_PRIMARY_ROLE,
			icon: 'group',
			show: false,
			disabled: false,
			click: (item) => {
				this.showConstraintRoleOverlay();
			},
		},
		{
			key: 'focusTicket',
			text: AppStrings.TICKET_EDIT_CENTER_ON_TICKET,
			singleText: 'Center on Ticket',
			multiText: 'Center on Tickets',
			icon: 'filter_center_focus',
			show: false,
			disabled: false,
			click: (item) => {
				this.focusTickets();
			},
		},
		{
			key: 'editActuals',
			text: AppStrings.TICKET_EDIT_EDIT_ACTUAL_VALUES,
			icon: 'edit',
			show: false,
			disabled: false,
			click: (item) => {
				this.transitionToActualizing();
			},
		},
		{
			key: 'revertToPlanned',
			text: AppStrings.TICKET_EDIT_REVERT_TO_PLANNED,
			icon: 'undo',
			show: false,
			disabled: false,
			click: (item) => {
				this.transitionToPlanned();
			},
		},
		{
			key: 'startWork',
			text: AppStrings.TICKET_EDIT_START_WORK,
			icon: 'play_arrow',
			show: true,
			disabled: false,
			click: () => {
				this.transitionToStarted();
			},
		},
		{
			key: 'makeUnassigned',
			text: AppStrings.TICKET_EDIT_RECLAIM_CONSTRAINT,
			icon: 'circle',
			show: false,
			disabled: false,
			click: (item) => {
				this.unassignConstraint();
			},
		},
		{
			key: 'duplicate',
			text: AppStrings.TICKET_EDIT_SAVE_AND_DUPLICATE,
			icon: 'file_copy',
			show: false,
			disabled: false,
			click: (item) => {
				this.duplicateTickets();
			},
		},
		{
			key: 'copyTicket',
			text: AppStrings.TICKET_EDIT_COPY_TO_CLIPBOARD,
			icon: 'content_paste',
			show: false,
			disabled: false,
			click: () => {
				this.copyTicket();
			},
		},
		{
			key: 'delete',
			text: AppStrings.TICKET_EDIT_DELETE,
			icon: 'delete',
			show: false,
			disabled: false,
			click: (item) => {
				this.deleteTickets();
			},
		},
	];
	// tslint:enable: max-line-length

	private inputSubject$ = new BehaviorSubject<EditTicketsCombined>(null);
	public list$: Observable<ActionMenuItem[]>;

	// public ticketCount = this.inputSubject.pipe(
	// 	map(etc => {
	// 		if(!etc || !etc.selectedList || !etc.isMulti){ return null; }
	// 		return etc.selectedList.size;
	// 	})
	// )

	public menuShown$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public _visible = false;
	public hasAccessToTicket$: Observable<boolean>;

	constructor(
		public ticketEditService: TicketEditService,
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef,
		private planState: PlanState,
		private constraintAcceptanceService: ConstraintAcceptanceService,
		private featureFlagManager: FeatureFlagManager,
		private logger: DefaultLoggerService,
		private planAccessCheck: PlanAccessCheck
	) {
		super(control, multiWrapper, cd);

		// Turning this into a property so we can reference it in the HTML (MOC-3281)
		this.hasAccessToTicket$ = this.planAccessCheck
			.hasAccessToTicket$(this.inputSubject$.asObservable().pipe(pluck('data')))
			.pipe(takeUntil(this.shutdownSubject));

		const isPlanReadOnly$ = this.planAccessCheck.hasRestrictedAccess$.pipe(takeUntil(this.shutdownSubject));

		const planAccessLevel$ = this.planAccessCheck.accessLevel$.pipe(takeUntil(this.shutdownSubject));

		this.list$ = combineLatest([
			//etc
			this.inputSubject$,
			//isProcoreEnabled
			this.planState.project.object$.pipe(map((project) => !!project.procoreProjectId)),
			//authedProjectMember
			this.planState.authedProjectMember$,
			//timelineTickets
			this.planState.timelineTickets.list$,
			//accessChecks
			this.hasAccessToTicket$,
			isPlanReadOnly$,
			this.ticketEditService.ticketEditPanelIsDisplayed$.asObservable(),
			this.ticketEditService.fileAttachEnabled$,
			planAccessLevel$,
		]).pipe(
			scan(
				(
					acc,
					value: [
						EditTicketsCombined,
						boolean,
						ProjectMember,
						Map<string, Ticket>,
						boolean,
						boolean,
						boolean,
						boolean,
						PlanAccessLevel
					]
				) => {
					this.updateList(
						acc,
						value[0],
						value[1],
						value[2],
						value[3],
						value[4],
						value[5],
						value[6],
						value[7],
						value[8]
					);

					return acc;
				},
				this.list
			)
		);
	}

	/** listed created on each update that represent a limited section of tickets
	 * impacted by specific menu options
	 */
	public subLists = {
		canAddPinList: [],
		canRemovePinList: [],
		canAddToTimelineList: [],
		canRemoveFromTimelineList: [],
	};

	public resetSubLists() {
		this.subLists = {
			canAddPinList: [],
			canRemovePinList: [],
			canAddToTimelineList: [],
			canRemoveFromTimelineList: [],
		};
	}

	// This function is called when one of _many_ subscriptions gets fired. So any subscriptions created inside this function will also
	// be created many times. Probably better to add to the massive combineLatest and pass in as an arg.
	private updateList(
		list: ActionMenuItem[],
		etc: EditTicketsCombined,
		isProcoreEnabled: boolean,
		authedProjectMember: ProjectMember,
		timelineTickets: Map<string, Ticket>,
		hasAccess: boolean,
		isReadOnly: boolean,
		ticketEditPanelIsDisplayed: boolean,
		fileAttachEnabled: boolean,
		planAccessLevel: PlanAccessLevel
	) {
		this.resetSubLists();

		const canAcceptConstraint =
			authedProjectMember &&
			authedProjectMember.$id === etc.data.assignedProjectMemberId &&
			etc.data.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED;
		const plannedPreviewOrActualized =
			etc.data.actualPhase === ActualPhase.actualized || etc.data.actualPhase === ActualPhase.plannedPreview;

		const isMulti = etc.isMulti;

		etc.selectedList.forEach((t) => {
			const canAdd = Ticket.canAddPin(t.view);
			const canRemove = Ticket.canRemovePin(t.view);
			if (canAdd) {
				this.subLists.canAddPinList.push(t);
			}
			if (canRemove) {
				this.subLists.canRemovePinList.push(t);
			}

			const combinedId = this.planState.timelineTickets.makeCombinedId(t.$id, this.planState.planId);
			const isTimelineTicket = !!timelineTickets.get(combinedId);
			if (isTimelineTicket) {
				this.subLists.canRemoveFromTimelineList.push(t);
			} else {
				this.subLists.canAddToTimelineList.push(t);
			}
		});

		const canAddPin = this.subLists.canAddPinList.length >= 1;
		const canRemovePin = this.subLists.canRemovePinList.length >= 1;
		const canAddToTimeline = this.subLists.canAddToTimelineList.length >= 1;
		const canRemoveFromTimeline = this.subLists.canRemoveFromTimelineList.length >= 1;

		const defaultNumber = etc && etc.selectedList ? etc.selectedList.size : null;

		function isAnyConstraint(type: CombinedTicketTypes) {
			return (
				type === TicketType.Constraint ||
				type === TicketDropdownTypes.CONSTRAINT_ACCEPTED ||
				type === TicketDropdownTypes.CONTRAINT_ASSIGNED
			);
		}

		function isPreAcceptedConstraint(type: CombinedTicketTypes) {
			return type === TicketDropdownTypes.CONTRAINT_ASSIGNED;
		}

		list.forEach((item) => {
			if (isMulti) {
				item.impactedTickets = defaultNumber;
			}
			switch (item.key) {
				case 'viewAttachments':
					// "viewAttachmentsMenuItem.show" needs to be updated whenever the menu's "View Attachments" menu item
					// needs to be hidden or shown.
					// That depends on two Observables:
					//
					// One: ticketEditService.ticketEditPanelIsDisplayed$
					//				 While the ticket-edit-panel is displayed, the "View Attachments" menu item should be hidden
					//				 since the panel is already visible.  When the panel is closed, the menu item should be shown again.
					//				 Although...
					// Two: ticketEditService.fileAttachEnabled$
					//				 The "View Attachments" menu item should only be shown if the FILE_ATTACH_ENABLED is true,
					//				 and project-level configuration doesn't disable it.
					//
					// Therefore the menu item is displayed iff (fileAttachEnabled$) and (not ticketPanelIsDisplayed)
					item.show = !isMulti && fileAttachEnabled && !ticketEditPanelIsDisplayed && etc.ticketsInPlanRegion;
					break;
				case 'addPin':
					item.show =
						etc.ticketsInPlanRegion &&
						!isAnyConstraint(etc.selectedType) &&
						(canAddPin || isMulti || etc.data.needsVariance) &&
						authedProjectMember.view.accessLevel === 'Admin';
					item.disabled =
						authedProjectMember.view.accessLevel !== 'Admin' ||
						etc.data.side === 'pull' ||
						etc.data.ticketsActualPhases.has(ActualPhase.actualized) ||
						etc.data.ticketsActualPhases.has(ActualPhase.actualizing) ||
						etc.data.ticketsActualPhases.has(ActualPhase.plannedPreview) ||
						isReadOnly ||
						!etc.ticketsInPlanRegion ||
						!canAddPin;
					if (isMulti) {
						item.impactedTickets = this.subLists.canAddPinList.length;
					}
					// if(!hasAccess || isReadOnly){ item.show = false; } //no access override
					break;
				case 'removePin':
					item.show =
						!isAnyConstraint(etc.selectedType) &&
						(canRemovePin || isMulti) &&
						authedProjectMember.view.accessLevel === 'Admin';
					item.disabled =
						authedProjectMember.view.accessLevel !== 'Admin' ||
						etc.data.side === 'pull' ||
						etc.data.actualPhase !== ActualPhase.planned ||
						isReadOnly ||
						!etc.ticketsInPlanRegion ||
						!canRemovePin;
					if (isMulti) {
						item.impactedTickets = this.subLists.canRemovePinList.length;
					}
					// if(!hasAccess || isReadOnly){ item.show = false; } //no access override
					break;
				case 'acceptConstraint':
					item.show = isAnyConstraint(etc.selectedType) && canAcceptConstraint;
					item.disabled = !hasAccess || isReadOnly;
					// if(!hasAccess || isReadOnly){ item.show = false; } //no access override
					break;
				case 'moreDetails':
					// MOC-3766
					item.show = isAnyConstraint(etc.selectedType);
					item.disabled = !etc.ticketsInPlanRegion;
					break;
				case 'linkToProcore':
					item.show = isAnyConstraint(etc.selectedType) && isProcoreEnabled && !etc.data.procoreRfiId;
					item.disabled = isProcoreEnabled && (!hasAccess || isReadOnly);
					// if(!hasAccess || isReadOnly){ item.show = false; } //no access override
					break;
				case 'procore':
					item.show = isAnyConstraint(etc.selectedType) && isProcoreEnabled && !!etc.data.procoreRfiId;
					item.disabled = !hasAccess || isReadOnly;
					break;
				case 'addToTimeline':
					item.show =
						(isAnyConstraint(etc.selectedType) || etc.selectedType === TicketType.Milestone) &&
						(canAddToTimeline || isMulti);
					if (!hasAccess || isReadOnly || !etc.ticketsInPlanRegion || !canAddToTimeline) {
						item.disabled = true;
					} //no access override
					if (isMulti) {
						item.impactedTickets = this.subLists.canAddToTimelineList.length;
					}
					break;
				case 'removeFromTimeline':
					item.show =
						(isAnyConstraint(etc.selectedType) || etc.selectedType === TicketType.Milestone) &&
						(canRemoveFromTimeline || isMulti);
					if (!hasAccess || isReadOnly || !etc.ticketsInPlanRegion || !canRemoveFromTimeline) {
						item.disabled = true;
					} //no access override
					if (isMulti) {
						item.impactedTickets = this.subLists.canRemoveFromTimelineList.length;
					}
					break;
				case 'changePrimaryRole':
					//pre-nonsense version
					// item.show = isPreAcceptedConstraint(etc.selectedType); //(isAnyConstraint(etc.selectedType));//isAnyConstraint(etc.selectedType);
					// item.disabled = !hasAccess || isReadOnly;
					//post-nonsense version
					item.show = isAnyConstraint(etc.selectedType); //isAnyConstraint(etc.selectedType);
					item.disabled = !isPreAcceptedConstraint(etc.selectedType) || !hasAccess || isReadOnly;
					// if(!hasAccess || isReadOnly){ item.show = false; } //no access override
					break;
				case 'focusTicket':
					if (etc.selectedList.size > 1) {
						item.text = item.multiText;
					} else {
						item.text = item.singleText;
					}
					item.show = etc.ticketsInPlanRegion;
					break;
				case 'editActuals':
					item.show = etc.data.actualPhase !== ActualPhase.planned; //any cases where actualFinish is there?
					item.disabled =
						etc.data.actualPhase === ActualPhase.actualizing ||
						!hasAccess ||
						planAccessLevel === PlanAccessLevel.archived;
					// if(!hasAccess){ item.show = false; } //no access override
					break;
				case 'revertToPlanned':
					item.show =
						plannedPreviewOrActualized ||
						etc.data.actualPhase === ActualPhase.actualizing ||
						etc.data.actualPhase === ActualPhase.started; //any cases where actualFinish is there?
					item.disabled = !hasAccess || isReadOnly;
					// if(!hasAccess || isReadOnly){ item.show = false; } //no access override
					break;
				case 'startWork':
					item.show =
						etc.selectedType === TicketType.Task &&
						etc.data.actualPhase === ActualPhase.planned &&
						etc.ticketsInPlanRegion;
					item.disabled = !hasAccess || planAccessLevel === PlanAccessLevel.archived;
					break;
				case 'duplicate':
					item.show = true;
					item.disabled = !hasAccess || (isReadOnly && etc.ticketsInPlanRegion); // || etc.data.actualPhase !== ActualPhase.planned;
					// if(!hasAccess || isReadOnly){ item.show = false; } //no access override
					break;
				case 'copyTicket':
					item.show = etc.ticketsInPlanRegion;
					item.disabled = !hasAccess || (isReadOnly && etc.ticketsInPlanRegion);
					break;
				case 'delete':
					item.show = !etc.ticketJustAdded;
					item.disabled =
						!hasAccess ||
						(isReadOnly && etc.ticketsInPlanRegion) ||
						etc.data.actualPhase !== ActualPhase.planned;
					// if(!hasAccess || isReadOnly){ item.show = false; } //no access override
					break;
				case 'makeUnassigned': //reclaim
					item.show = isAnyConstraint(etc.selectedType);
					item.disabled =
						isReadOnly ||
						plannedPreviewOrActualized ||
						(etc.data.constraintState === CONSTRAINT_ACCEPTANCE_STATE.UNASSIGNED &&
							!(
								etc.data.requestedByProjectMemberId === authedProjectMember.$id ||
								authedProjectMember.view.accessLevel === 'Admin' ||
								etc.data.creatorId === authedProjectMember.$id
							));
					break;
				default:
					this.logger.log('reeeeee');
					break;
			}
		});
	}

	public ngOnInit() {
		super.ngOnInit();
	}

	public acceptConstraint(): void {
		this.constraintAcceptanceService.setAccepted(this.inputSubject$.getValue());
		this.closeMenu();
	}

	public unassignConstraint(): void {
		this.constraintAcceptanceService.setUnassigned(this.inputSubject$.getValue());
		this.closeMenu();
	}

	// Toggles the dropdown menu
	public toggleMenu() {
		this._visible = !this._visible;
		this.menuShown$.next(this._visible);
		if (this._visible) {
			analyticsEventsService.clickTicketMenu();
		}
	}

	public closeMenu(): void {
		this._visible = false;
		this.menuShown$.next(this._visible);
	}

	public viewAttachments(): void {
		this.ticketEditService.showTicketAttachments();
		this.closeMenu();
	}

	public addPin(): void {
		this.ticketEditService.markFormAsDirty();
		this.ticketEditService.addEditPin();
		this.closeMenu();
	}

	public removePin(): void {
		this.ticketEditService.markFormAsDirty();
		this.ticketEditService.removeEditPin();
		this.closeMenu();
	}

	public constraintMoreDetails(): void {
		const etc = this.inputSubject$.getValue();
		this.ticketEditService.openMoreDetailsFromEdit(etc);
	}

	public constraintLinkToProcore(): void {
		this.ticketEditService.setOverlay(EditOverlay.procoreLink);
	}

	public toggleOnTimeline(): void {
		const tickets = this.convertTicketArrayToMap(this.subLists.canAddToTimelineList);
		this.ticketEditService.markFormAsDirty();
		this.ticketEditService.toggleEditTimeline(tickets);
	}
	public toggleOffTimeline(): void {
		const tickets = this.convertTicketArrayToMap(this.subLists.canRemoveFromTimelineList);
		this.ticketEditService.markFormAsDirty();
		this.ticketEditService.toggleEditTimeline(tickets);
	}

	public showProcore(): void {
		this.ticketEditService.setOverlay(EditOverlay.procoreStatus);
	}

	public showConstraintRoleOverlay(): void {
		this.ticketEditService.setOverlay(EditOverlay.constraintRoleSelector);
	}

	public duplicateTickets(): void {
		this.ticketEditService.duplicateEditTickets();
	}

	public copyTicket(): void {
		this.ticketEditService.copyTicket();
	}

	public deleteTickets(): void {
		this.ticketEditService.deleteEditTickets();
	}

	public transitionToPlanned(): void {
		this.ticketEditService.markFormAsDirty();
		this.ticketEditService.transitionToPlanned();
		analyticsEventsService.revertToPlanned();
	}

	public transitionToStarted(): void {
		this.ticketEditService.markFormAsDirty();
		this.ticketEditService.transitionToStarted();
	}

	private isAnyConstraint(type: CombinedTicketTypes) {
		return (
			type === TicketType.Constraint ||
			type === TicketDropdownTypes.CONSTRAINT_ACCEPTED ||
			type === TicketDropdownTypes.CONTRAINT_ASSIGNED
		);
	}

	public transitionToActualizing(): void {
		// MOC-3782 - Open 'More Details' when using "Edit Actuals" if this is a constraint
		// MOC-4314 - Constraints now use same actualizing flow as tasks/milestones
		this.ticketEditService.transitionToActualizing();
		// const etc = this.inputSubject.getValue();
		// if (this.isAnyConstraint(etc.selectedType)) {
		// 	this.constraintMoreDetails();
		// } else {
		// 	this.ticketEditService.transitionToActualizing();
		// }
	}

	public focusTickets(): void {
		this.ticketEditService.focusTicket(this.inputSubject$.getValue());

		Logging.notice(AppStrings.CENTER_ON_TICKET_MESSAGE);
		analyticsEventsService.clickFocusTicket();
	}

	private convertTicketArrayToMap(arr: Ticket[]) {
		const tickets = new Map<string, Ticket>();
		arr.forEach((t) => tickets.set(t.$id, t));
		return tickets;
	}
}
